import { jsx, jsxs } from "react/jsx-runtime";
import polarToSpecialCartesian from "../utils/polarToSpecialCartesian";
import relaxLabelAngles from "./relaxLabelAngles";
import withHover from "../../helperComponents/withHover";
import "./style.css";
import React from "react";
import { cloneDeep, clamp, noop } from "lodash-es";
import { avoidOverlapWith } from "../drawAnnotations";
const fontWidthToFontSize = 1.75;
export const getTextLengthWithCollapseSpace = (text, collapseWhiteSpace = true) => {
  let displayText = text || "Unlabeled";
  if (collapseWhiteSpace) {
    displayText = displayText.replaceAll(/\s+/g, " ");
  }
  let len = displayText.length;
  const nonEnInputReg = /[^\x00-\xff]+/g;
  const nonEnStrings = displayText.match(nonEnInputReg) || [];
  nonEnStrings.forEach((str) => len += str.length * 0.5);
  return len;
};
function Labels({
  labels = [],
  extraSideSpace,
  smartCircViewLabelRender,
  radius: outerRadius,
  editorName,
  noRedux,
  rotationRadians,
  textScalingFactor,
  labelLineIntensity,
  labelSize = 8,
  fontHeightMultiplier = 2.4,
  circularViewWidthVsHeightRatio,
  //width of the circular view
  condenseOverflowingXLabels = true
  //set to true to make labels tha
}) {
  if (!labels.length) {
    return {
      component: null,
      height: 15
    };
  }
  const originalOuterRadius = outerRadius;
  outerRadius += smartCircViewLabelRender ? 10 : 25;
  const radius = outerRadius;
  const outerPointRadius = outerRadius - 20;
  const fontWidth = labelSize * (textScalingFactor < 1 ? textScalingFactor : 1);
  const fontHeight = fontWidth * clamp(fontHeightMultiplier, 1.5, 3.5);
  const labelPoints = labels.map(function(label) {
    const {
      annotationCenterAngle: _annotationCenterAngle,
      annotationCenterRadius
    } = label;
    const annotationCenterAngle = _annotationCenterAngle + (rotationRadians || 0);
    return {
      ...label,
      width: getTextLengthWithCollapseSpace(label.text) * fontWidth,
      //three points define the label:
      innerPoint: {
        ...polarToSpecialCartesian(
          annotationCenterRadius,
          annotationCenterAngle
        ),
        radius: annotationCenterRadius,
        angle: annotationCenterAngle
      },
      truncatedInnerPoint: {
        ...polarToSpecialCartesian(
          outerPointRadius - 15,
          annotationCenterAngle
        ),
        radius: outerPointRadius - 15,
        angle: annotationCenterAngle
      },
      outerPoint: {
        ...polarToSpecialCartesian(outerPointRadius, annotationCenterAngle),
        radius: outerPointRadius,
        angle: annotationCenterAngle
      },
      ...polarToSpecialCartesian(radius, annotationCenterAngle),
      radius: radius + 10,
      angle: annotationCenterAngle
    };
  }).map(function(label) {
    label.labelAndSublabels = [label];
    label.labelIds = { [label.id]: true };
    return label;
  });
  let maxRadius = 1;
  const groupedLabels = relaxLabelAngles(labelPoints, fontHeight, outerRadius).filter((l) => !!l).map((originalLabel) => {
    if (smartCircViewLabelRender) {
      const newR = Math.sqrt(
        Math.pow(
          Math.abs(originalLabel.x) + Math.max(0, originalLabel.text.length * 11 - extraSideSpace / 2),
          2
        ) + Math.pow(Math.abs(originalLabel.y), 2)
      );
      if (newR > maxRadius) maxRadius = newR;
    }
    if (originalLabel.highPriorityLabel) {
      return originalLabel;
    }
    const _highPrioritySublabel = originalLabel.labelAndSublabels.find(
      (l) => l.highPriorityLabel
    );
    if (_highPrioritySublabel) {
      const highPrioritySublabel = cloneDeep(_highPrioritySublabel);
      [
        "angle",
        "annotationCenterAngle",
        "annotationCenterRadius",
        "innerPoint",
        "labelAndSublabels",
        "labelIds",
        "outerPoint",
        "radius",
        "truncatedInnerPoint",
        "x",
        "y"
      ].forEach((k) => {
        highPrioritySublabel[k] = originalLabel[k];
      });
      delete originalLabel.labelAndSublabels;
      return highPrioritySublabel;
    }
    return originalLabel;
  });
  window.isLabelGroupOpen = false;
  return {
    component: /* @__PURE__ */ jsx(
      "g",
      {
        className: "veLabels ve-monospace-font",
        transform: `rotate(-${rotationRadians * 180 / Math.PI})`,
        children: /* @__PURE__ */ jsx(
          DrawGroupedLabels,
          {
            ...{
              editorName,
              noRedux,
              groupedLabels,
              circularViewWidthVsHeightRatio,
              fontWidth,
              fontHeight,
              condenseOverflowingXLabels,
              outerRadius,
              labelLineIntensity
            }
          }
        )
      },
      "veLabels"
    ),
    //we use the <use> tag to position the hovered label group at the top of the stack
    //point events: none is to fix a click bug..
    //http://stackoverflow.com/questions/24078524/svg-click-events-not-firing-bubbling-when-using-use-element
    height: smartCircViewLabelRender ? Math.min(105, maxRadius - originalOuterRadius) : 120
  };
}
export default Labels;
const DrawLabelGroup = withHover(function({
  hovered,
  className,
  label,
  labelAndSublabels,
  fontWidth,
  noRedux,
  fontHeight,
  outerRadius,
  onMouseLeave,
  onMouseOver,
  editorName,
  circularViewWidthVsHeightRatio,
  condenseOverflowingXLabels,
  hoveredId,
  labelLineIntensity,
  // labelIds,
  multipleLabels
  // isIdHashmap,
}) {
  let { text = "Unlabeled" } = label;
  let groupLabelXStart;
  if (label.labelAndSublabels && label.labelAndSublabels.length > 1) {
    text = "+" + (label.labelAndSublabels.length - 1) + "," + text;
  }
  const labelLength = getTextLengthWithCollapseSpace(text) * fontWidth;
  const labelOnLeft = label.angle > Math.PI;
  let labelXStart = label.x - (labelOnLeft ? labelLength : 0);
  const maxDistance = (outerRadius + 90) * Math.max(1, circularViewWidthVsHeightRatio);
  if (condenseOverflowingXLabels) {
    const distancePastBoundary = Math.abs(label.x + (labelOnLeft ? -labelLength : labelLength)) - maxDistance;
    if (distancePastBoundary > 0) {
      const numberOfCharsToChop = Math.ceil(distancePastBoundary / fontWidth) + 2;
      text = text.slice(0, -numberOfCharsToChop) + "..";
      groupLabelXStart = labelXStart + (labelOnLeft ? distancePastBoundary : -distancePastBoundary);
      labelXStart += labelOnLeft ? distancePastBoundary : 0;
    }
  }
  const dy = fontHeight;
  const textYStart = label.y + dy / 2;
  let content;
  const labelClass = ` veLabelText veLabel veCircularViewLabelText clickable ${label.color} `;
  if ((multipleLabels || groupLabelXStart !== void 0) && hovered) {
    const maxLabelLength = labelAndSublabels.reduce(function(currentLength, { text: text2 = "Unlabeled" }) {
      const _textLength = getTextLengthWithCollapseSpace(text2);
      if (_textLength > currentLength) {
        return _textLength;
      }
      return currentLength;
    }, 0);
    const maxLabelWidth = maxLabelLength * fontWidth;
    labelXStart = label.x - (labelOnLeft ? maxLabelWidth : 0);
    let distancePastBoundary = Math.abs(label.x + (labelOnLeft ? -maxLabelWidth : maxLabelWidth)) - maxDistance;
    let lableRectWidth = maxLabelWidth - 14;
    if (maxLabelWidth > maxDistance * 2) {
      labelXStart = -maxDistance;
      lableRectWidth = maxDistance * 2 - 24;
      distancePastBoundary = maxLabelWidth - maxDistance * 2;
    } else if (distancePastBoundary > 0) {
      labelXStart += labelOnLeft ? distancePastBoundary : -distancePastBoundary;
      distancePastBoundary = 0;
    }
    window.isLabelGroupOpen = true;
    let hoveredLabel;
    let truncatedLabelAndSublabels;
    if (distancePastBoundary > 0) {
      truncatedLabelAndSublabels = labelAndSublabels.map((lable) => {
        const labelWidth = getTextLengthWithCollapseSpace(lable.text) * fontWidth;
        const truncatedText = labelWidth >= lableRectWidth ? lable.text.slice(
          0,
          -Math.ceil((labelWidth - lableRectWidth) / fontWidth) - 2
        ) + ".." : lable.text;
        return {
          ...lable,
          text: truncatedText
        };
      });
    }
    labelAndSublabels.some(function(label2) {
      if (label2.id === hoveredId) {
        hoveredLabel = label2;
        return true;
      }
      return false;
    });
    if (!hoveredLabel) {
      hoveredLabel = label;
    }
    let labelYStart = label.y;
    const labelGroupHeight = labelAndSublabels.length * dy;
    const labelGroupBottom = label.y + labelGroupHeight;
    if (labelGroupBottom > outerRadius + 20) {
      labelYStart -= (label.labelAndSublabels.length - 1) * dy;
      if (labelYStart < -outerRadius) {
      }
    }
    const line = LabelLine(
      [
        hoveredLabel.innerPoint,
        // hoveredLabel.labelAndSublabels &&
        // hoveredLabel.labelAndSublabels.length > 0
        //   ? hoveredLabel.outerPoint
        //   : {},
        label
      ],
      { style: { opacity: 1 }, strokeWidth: 2 }
    );
    content = [
      line,
      /* @__PURE__ */ jsx(PutMyParentOnTop, { editorName, children: /* @__PURE__ */ jsxs("g", { className: className + " veLabel topLevelLabelGroup", children: [
        /* @__PURE__ */ jsx(
          "rect",
          {
            onMouseOver: cancelFn,
            x: labelXStart - 4,
            y: labelYStart - dy / 2,
            width: Math.max(0, lableRectWidth + 24),
            height: labelGroupHeight + 4,
            fill: "white",
            stroke: "black"
          }
        ),
        /* @__PURE__ */ jsx(
          "text",
          {
            x: labelXStart,
            y: labelYStart,
            style: {
              fontSize: fontWidth * fontWidthToFontSize,
              fontStyle: label.fontStyle,
              fontWeight: label.fontWeight
            },
            children: (truncatedLabelAndSublabels || labelAndSublabels).map(
              function(label2, index) {
                return /* @__PURE__ */ jsx(
                  DrawGroupInnerLabel,
                  {
                    isSubLabel: true,
                    noRedux,
                    editorName,
                    logHover: true,
                    className: (label2.className || "") + labelClass + " veDrawGroupInnerLabel",
                    id: label2.id,
                    ...{ labelXStart, label: label2, fontWidth, index, dy }
                  },
                  "labelItem" + index
                );
              }
            )
          }
        )
      ] }) }, "gGroup")
    ];
  } else {
    content = [
      /* @__PURE__ */ jsx("g", { hidden: true, style: { display: "none" }, children: label.title || label.text }, "labeltitle"),
      /* @__PURE__ */ jsx(
        "text",
        {
          "data-title": label.title || label.text,
          ...avoidOverlapWith,
          x: labelXStart,
          textLength: Math.max(
            0,
            getTextLengthWithCollapseSpace(text) * fontWidth
          ),
          lengthAdjust: "spacing",
          className: labelClass + label.className + (hovered ? " veAnnotationHovered" : ""),
          y: textYStart,
          style: {
            fontSize: fontWidth * fontWidthToFontSize,
            fontStyle: label.fontStyle,
            fontWeight: label.fontWeight,
            fill: label.color || "black"
            // stroke: label.color ? label.color : "black"
          },
          children: text
        },
        "text"
      ),
      LabelLine(
        [
          label.innerPoint,
          // hovered || label.annotationType === "part" //because parts live on the outside of the sequence, we don't need to show the truncated point, we can just point to them directly
          //   ? label.innerPoint
          //   : label.truncatedInnerPoint,
          label.outerPoint,
          label
        ],
        hovered ? { style: { opacity: 1 }, strokeWidth: 2 } : { style: { opacity: labelLineIntensity } }
      )
    ];
  }
  return /* @__PURE__ */ jsx(
    "g",
    {
      ...{ onMouseLeave, onMouseOver },
      ...{
        onClick: label.onClick,
        onDoubleClick: label.onDoubleClick || noop,
        onContextMenu: label.onContextMenu || noop
      },
      children: content
    }
  );
});
function LabelLine(pointArray, options) {
  let points = "";
  pointArray.forEach(function({ x, y }) {
    if (!x && x !== 0) return;
    points += `${x},${y} `;
  });
  return /* @__PURE__ */ jsx(React.Fragment, { children: /* @__PURE__ */ jsx(
    "polyline",
    {
      ...{
        points,
        stroke: "black",
        fill: "none",
        strokeWidth: 1,
        className: "veLabelLine",
        ...options
      }
    },
    "polyline-long"
  ) }, "labelLine");
}
const DrawGroupInnerLabel = withHover(
  ({ className, labelXStart, label, fontWidth, onMouseOver, index, dy }) => {
    return /* @__PURE__ */ jsxs(
      "tspan",
      {
        "data-title": label.title,
        ...avoidOverlapWith,
        x: labelXStart,
        textLength: Math.max(
          0,
          getTextLengthWithCollapseSpace(label.text) * fontWidth
        ),
        lengthAdjust: "spacing",
        onClick: label.onClick,
        onDoubleClick: (e) => {
          e.stopPropagation();
          label.onDoubleClick && label.onDoubleClick(e);
        },
        onContextMenu: label.onContextMenu,
        dy: index === 0 ? dy / 2 : dy,
        style: {
          fill: label.color ? label.color : "black",
          fontStyle: label.fontStyle,
          fontWeight: label.fontWeight
        },
        ...{ onMouseOver },
        className,
        children: [
          /* @__PURE__ */ jsx("g", { hidden: true, style: { display: "none" }, children: label.title }),
          label.text
        ]
      }
    );
  }
);
const DrawGroupedLabels = function DrawGroupedLabelsInner({
  groupedLabels,
  circularViewWidthVsHeightRatio,
  fontWidth,
  noRedux,
  fontHeight,
  condenseOverflowingXLabels,
  outerRadius,
  editorName,
  labelLineIntensity
}) {
  return groupedLabels.map(function(label, i) {
    const { labelAndSublabels, labelIds } = label;
    const multipleLabels = labelAndSublabels.length > 1;
    return /* @__PURE__ */ jsx(
      DrawLabelGroup,
      {
        id: labelIds,
        ...{
          label,
          noRedux,
          passHoveredId: true,
          //needed to get the hoveredId
          isLabelGroup: true,
          className: "DrawLabelGroup",
          multipleLabels,
          labelAndSublabels,
          labelIds,
          circularViewWidthVsHeightRatio,
          fontWidth,
          editorName,
          fontHeight,
          condenseOverflowingXLabels,
          outerRadius,
          labelLineIntensity
        }
      },
      i
    );
  });
};
function cancelFn(e) {
  e.stopPropagation();
}
class PutMyParentOnTop extends React.Component {
  componentDidMount() {
    const { editorName } = this.props;
    try {
      const escapedEditorName = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(editorName) : editorName.replace(/([!"#$%&'()*+,./:;<=>?@\\[\]^`{|}~])/g, "\\$1");
      const el = document.querySelector(
        `.veEditor.${escapedEditorName} .topLevelLabelGroup`
      );
      if (!el) return;
      const parent = el.parentElement.parentElement;
      const i = Array.prototype.indexOf.call(parent.children, el.parentElement);
      parent.insertBefore(parent.children[i], null);
    } catch (error) {
      console.warn(
        "OVE-975239 - hit an error trying to re-order labels:",
        error
      );
    }
  }
  render() {
    return this.props.children;
  }
}
